import { secTsToRegionalDate } from '@spx-portal/shared-utils';
import dayjs from 'dayjs';
import { FormInstance } from 'ssc-ui-react/typings/components/form/types';

import { createSelectors } from '@/store/constructor/createSelectors';
import { createStore } from '@/store/constructor/createStore';

import { getGroupThroughputOverview, GetGroupThroughputOverviewParams } from '../api';
import { CalculateMethod } from '../const';
import { OriginGroupItem, TargetGroup } from '../defined';

type State = {
  lastUpdate: {
    startTime: number;
    endTime: number;
  };
  timeIntervals: string[];
  lastFetchUpdateTime: number;
  calculateMethod?: CalculateMethod;
  groupListLoading: boolean;
  groupList: OriginGroupItem[];
  targetGroup: TargetGroup;
  cacheState?: Record<number, 'pending' | 'ready'>;
  overviewForm?: FormInstance;
}

type Action = {
  setOverviewForm: (overviewForm?: FormInstance) => void;
  getOverviewForm: () => FormInstance | undefined;
  getLastUpdate: () => { startTime: number; endTime: number };
  setLastUpdate: (lastUpdate: { startTime: number; endTime: number }) => void;
  getGroupListAsync: (params?: GetGroupThroughputOverviewParams) => Promise<OriginGroupItem[]>;
  getGroupList: () => OriginGroupItem[];
  setGroupList: (groupList?: OriginGroupItem[]) => void;
  getTargetGroup: () => TargetGroup;
  setTargetGroup: (targetGroup?: TargetGroup) => void;
  setTimeIntervals: (timeIntervals: string[]) => void;
}

const useWorkstationOverviewStore = createSelectors(
  createStore<State & Action>((set, get) => {
    return {
      overviewForm: undefined,
      lastUpdate: {
        startTime: 0,
        endTime: 0,
      },
      timeIntervals: [],
      lastFetchUpdateTime: 0,
      calculateMethod: undefined,
      cacheState: undefined,
      groupListLoading: false,
      groupList: [],
      targetGroup: { groupId: 0, groupName: '', workstation_group: '', groupIndex: 0 },
      groupDropdownList: [],
      groupDropdownListLoading: false,
      setOverviewForm: (overviewForm) => set({ overviewForm }),
      getOverviewForm: () => get().overviewForm,
      getLastUpdate: () => get().lastUpdate,
      setLastUpdate: (lastUpdate) => set({ lastUpdate }),
      getGroupListAsync: async (params) => {
        try {
          set({ groupListLoading: true });
          const defaultParams = get().overviewForm?.getFieldsValue();
          const res = await getGroupThroughputOverview({ ...defaultParams, ...params });
          const list = res.data?.list || [];
          const startTime = res.data?.time_begin || 0;
          const endTime = res.data?.time_end || 0;
          // keep the same order if refresh
          const curGroupListMap = get().groupList.reduce((acc, cur, index) => {
            if (cur.workstation_group) {
              acc[cur.workstation_group] = index + 1;
            }
            return acc;
          }, {} as Record<string, number>);
          const existGroupList = list
            .filter(item => item.workstation_group ? curGroupListMap[item.workstation_group] : false)
            .sort((a, b) => {
              const aIndex = a.workstation_group ? curGroupListMap[a.workstation_group] : 0;
              const bIndex = b.workstation_group ? curGroupListMap[b.workstation_group] : 0;
              return aIndex - bIndex;
            });
          const nonExistGroupList = list.filter(item => !item.workstation_group || !curGroupListMap[item.workstation_group]);
          const newGroupList = existGroupList.concat(nonExistGroupList);
          set({
            groupList: newGroupList,
            targetGroup: {
              groupId: list[0]?.workstation_group_id || 0,
              groupName: list[0]?.workstation_group_name || '',
              workstation_group: list[0]?.workstation_group || '',
              groupIndex: 0,
            },
            lastUpdate: {
              startTime: startTime ? dayjs(secTsToRegionalDate(startTime)).unix() : 0,
              endTime: endTime ? dayjs(secTsToRegionalDate(endTime)).unix() : 0,
            },
            lastFetchUpdateTime: dayjs().unix(),
            calculateMethod: res.data?.calculate_method,
          });
          return res.data?.list || [];
        } catch (error) {
          console.error('[getGroupListAsync] error: ', error);
          return [];
        } finally {
          set({ groupListLoading: false });
        }
      },
      getGroupList: () => get().groupList || [],
      setGroupList: (groupList) => set({ groupList }),
      getTargetGroup: () => get().targetGroup || { groupId: 0, groupName: '', groupIndex: 0 },
      setTargetGroup: (targetGroup) => set({ targetGroup }),
      setTimeIntervals: (timeIntervals) => set({ timeIntervals }),
    };
  }),
);

export default useWorkstationOverviewStore;
